import { Component, OnDestroy, OnInit } from '@angular/core';
import { addMonths, differenceInCalendarMonths, differenceInMonths, format, parse, startOfMonth } from 'date-fns';
import * as _ from 'lodash-es';
import { NgxSpinnerService } from 'ngx-spinner';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SubSink } from 'subsink';
import { PpsUtilityService } from '../services/pps-utility.service';
import { TimesheetService } from '../services/timesheet.service';
import { Project } from '../shared/models/project';
import { Timesheet } from '../shared/models/timesheet';
import { TimesheetSearchModel } from '../shared/models/timesheet-search-model';

@Component({
  selector: 'pps-project-timesheet',
  templateUrl: './project-timesheet.component.html',
  styles: [
  ],
})
export class ProjectTimesheetComponent implements OnInit, OnDestroy {
  private subs = new SubSink();
  private gridApi;
  project: Project;
  minDate: string;
  maxDate: string;
  hoursByMonth: any = {};
  monthList: string[] = [];
  messages: any[] = [];

  timesheets: Timesheet[] = [];
  context:any = {};
  gridHeaders = [];
  gridOptions = {
    defaultColDef:{
      resizable: true
    }
  };

  constructor(private dialogConfig: DynamicDialogConfig,
              private dialogRef: DynamicDialogRef,
              private timesheetService: TimesheetService,
              private spinner: NgxSpinnerService,
              public util: PpsUtilityService) { }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.context = { componentParent: this };
    this.gridHeaders = this.getColumnDefs();
    this.setData(this.dialogConfig.data.project);

    this.loadProjectTimesheet();
  }

  onGridReady(params) {
    this.gridApi = params.api;
  }  

  loadProjectTimesheet(){
    this.spinner.show();
    this.timesheets = [];
  
    this.subs.sink = this.timesheetService
      .getProjectTimesheets(this.project.id)
      .subscribe(response => {
        if (response.succeed){
          this.timesheets = response.result;
          if (this.timesheets.length > 0){
            this.maxDate = this.util.getDateString(new Date(Math.max.apply(null, this.timesheets.map((o) => o.day))));
            this.minDate = this.util.getDateString(new Date(Math.min.apply(null, this.timesheets.map((o) => o.day))));
            this.groupHoursByMonth();
          }else{
            this.messages = [{severity:'error', summary:'', detail:`No timesheet records found for project ${this.project.projectNo}.`}];
          }
        }
      }, () => {}, () => {
        this.spinner.hide();
      });
  }

  private groupHoursByMonth(){
    this.hoursByMonth = {};
    this.monthList = [];

    let totalHours = 0;
    this.timesheets.forEach(ts => {
      let month = format(ts.day, "MMM yyyy");
      if (this.hoursByMonth.hasOwnProperty(month)){
        this.hoursByMonth[month] += ts.hours;
      }else{
        this.hoursByMonth[month] = ts.hours;
      }

      totalHours += ts.hours;
    });

    let sDate = this.util.getDate(this.minDate);
    let eDate = this.util.getDate(this.maxDate)
    let months = differenceInCalendarMonths(eDate, sDate);
    let month = startOfMonth(sDate);
    let monthKey = format(month, "MMM yyyy");

    if (this.hoursByMonth[monthKey]){
      this.monthList.push(monthKey);
    }

    for (let i = 0; i < months; i ++){
      month = addMonths(month, 1);
      monthKey = format(month, "MMM yyyy");

      if (this.hoursByMonth[monthKey]){
        this.monthList.push(monthKey);
      }
    }

    for (var m in this.hoursByMonth){
      this.hoursByMonth[m] = this.util.formatNumber(this.hoursByMonth[m], 2);
    }

    let budgetHours = this.project.budgetHour === null ? "Not set" : this.util.formatNumber(this.project.budgetHour, 2);

    if (this.monthList.length > 1){
      let monthKey = "Total Hours / Budget Hours";
      this.monthList.push(monthKey);
      this.hoursByMonth[monthKey] = this.util.formatNumber(totalHours, 2) + " / " + budgetHours;
    }

    if (this.monthList.length === 1){
      var oldMonthKey = this.monthList[0];
      let monthKey = this.monthList[0] + " / Project Budget Hours";
      this.monthList[0] = monthKey;
      this.hoursByMonth[monthKey] = this.hoursByMonth[oldMonthKey] + " / " + budgetHours;
    }
  }

  setData(project: Project){
    this.project = project;
  }

  cancel(){
    this.dialogRef.close();
  }


  private getColumnDefs(){
    return [
      {
        headerName: 'Date',
        field:'day',
        sortable: true,
        width: 70,
        comparator: (date1, date2) => this.util.dateComparator(date1, date2),
        valueGetter: params => this.util.getDateString(params.data[params.colDef.field])
      },
      {
        headerName: 'Day',
        field:'day',
        sortable: true,
        width: 50,
        valueGetter: this.getWeekDay,
        // Sort day be week day instead of string
        comparator: (va, vb, na, nb, inverted) => {
          let a = this.util.getDate(na.data.day).getDay();
          let b = this.util.getDate(nb.data.day).getDay();

          if (a === b) return 0;
          return (a > b) ? 1 : -1;
        }
      },
      {
        headerName: 'Employee',
        field:'employee',
        sortable: true,
        width: 140,
        valueGetter: params => params.context.componentParent.util.getEmployeeName(params.data[params.colDef.field])
      },
      {
        headerName: 'Position',
        field:'employee.position.title',
        sortable: true,
        width: 140
      },
      {
        headerName: 'Hours',
        field:'hours',
        sortable: true,
        width: 80,
        cellStyle:this.util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        cellRenderer: params => params.context.componentParent.util.formatNumber(params.data[params.colDef.field], 2)
      },
      {
        headerName: 'Shift',
        field:'shift.code',
        sortable: true,
        width: 70,
        cellStyle:{borderRightColor:'#eeeeee'}
      },
      {
        headerName: 'Group',
        field:'employee.workGroup.name',
        sortable: true,
        width: 250
      },
      {
        headerName: 'Note',
        field:'note',
        sortable: true,
        width: 180
      },
      {
        headerName: 'Company',
        field:'employee.employer.employerName',
        sortable: true,
        width: 80,
        cellStyle:{borderRightColor:'#eeeeee'}
      }
    ];
  }  

  private getWeekDay(params){
    var dateValue = params.data[params.colDef.field];
    if (dateValue){
      return format(dateValue, "E");
    }
  }
}
